export type ScheduleAddress = {
  address: string;
  city: string;
  state: string;
  zipcode: string;
};

const COUNTRY = /^(us|usa|united states(?: of america)?)$/i;
const STATE_ZIP = /^([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/;
/**
 * A unit Google Places emits as its own part: "208", "#208", "Suite 208",
 * "Ste 208", "Unit 208". Shown the way the site already writes units
 * ("1320 E 7th St #136").
 */
const UNIT = /^(?:(?:suite|ste\.?|unit)\s*)?#?\s*(\d+[A-Za-z]?)$/i;

/**
 * Split an Openings schedule address ("street[, unit], city, ST zip[, country]")
 * into the Eddie `Location` fields. Read from the right: the country is
 * dropped, the last part must be "ST zip", the one before it is the city, and
 * everything left is the street line. A unit part is folded into the street
 * as "#208" so it is never mistaken for the city (Sawtelle's Google Places
 * address is "11668 West Olympic Boulevard, 208, Los Angeles, …").
 *
 * An address without a recognisable "ST zip" part keeps the old positional
 * read; any missing field takes the registry fallback.
 */
export const parseScheduleAddress = (
  raw: string | null | undefined,
  fallback: ScheduleAddress,
): ScheduleAddress => {
  const parts = (raw ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length > 0 && COUNTRY.test(parts[parts.length - 1])) parts.pop();

  const stateZip = STATE_ZIP.exec(parts[parts.length - 1] ?? "");
  if (!stateZip || parts.length < 3) {
    const [state = fallback.state, zipcode = fallback.zipcode] = (parts[2] ?? "")
      .split(" ")
      .filter(Boolean);
    return {
      address: parts[0] || fallback.address,
      city: parts[1] || fallback.city,
      state,
      zipcode,
    };
  }

  const street = parts
    .slice(0, -2)
    .reduce((line, part) => {
      const unit = UNIT.exec(part);
      return unit ? `${line} #${unit[1]}` : `${line}, ${part}`;
    });

  return {
    address: street,
    city: parts[parts.length - 2],
    state: stateZip[1].toUpperCase(),
    zipcode: stateZip[2],
  };
};
